import { Link } from "react-router-dom";
import { ArrowRight, AlertCircle, BedDouble, Sofa, UtensilsCrossed } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const BREAKDOWN = [
  { icon: Sofa, label: "Living Room", example: "Couch, coffee table, lamp & accent chair", price: "from $180" },
  { icon: BedDouble, label: "Bedroom", example: "Bed frame, mattress, dresser & nightstand", price: "from $170" },
  { icon: UtensilsCrossed, label: "Dining & Essentials", example: "Table, chairs, dishes, bedding & towels", price: "from $150" },
];

export default function ShopFurniture() {
  return (
    <div data-testid="shop-furniture-page">
      <PageHero overline="Shop Affordable Furniture" title="SHOP WITH" italic="dignity." testid="shop-headline">
        Browse donated furniture and household essentials at deeply affordable prices — and choose the pieces that
        make your house feel like home.
      </PageHero>

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <FadeUp>
          <div data-testid="not-free-notice" className="flex flex-col gap-4 rounded-2xl border-2 border-burgundy bg-burgundy/5 p-8 sm:flex-row sm:items-start">
            <AlertCircle className="h-9 w-9 shrink-0 text-burgundy" />
            <div>
              <h2 className="font-serif text-2xl text-burgundy sm:text-3xl">THIS IS NOT A FREE FURNITURE GIVEAWAY.</h2>
              <p className="mt-3 text-sm leading-relaxed text-moss sm:text-base">
                Fresh Start Furnishings offers donated furniture and household items at deeply affordable prices. The
                funds collected help support storage, truck rentals, transportation, pickups, cleaning, delivery,
                furniture representatives, and other program expenses — so the next family gets their fresh start too.
                That said, thanks to generous community donations, we're sometimes able to provide furniture to
                families <strong className="text-ink">completely free of charge</strong>, depending on available
                funding and household circumstances.
              </p>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div data-testid="affordability-promise" className="mt-12 rounded-3xl border border-linen bg-olive p-10 text-center text-cream sm:p-14">
            <Overline className="!text-gold">Our Affordability Promise</Overline>
            <p className="mt-4 font-serif text-6xl tracking-tight sm:text-8xl">
              <span className="italic text-gold">$500</span>
            </p>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-cream/80 sm:text-base">
              Our goal is to help families furnish an entire home for no more than $500, based on household needs,
              available inventory, and program guidelines. Individual items are priced separately at low, affordable prices.
            </p>
          </div>
        </FadeUp>

        <div className="mt-14">
          <FadeUp className="text-center">
            <Overline>Example Pricing</Overline>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-olive">What a whole home can look like</h2>
          </FadeUp>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {BREAKDOWN.map((b, i) => (
              <FadeUp key={b.label} delay={i * 0.12}>
                <div data-testid={`pricing-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bento-card h-full text-center">
                  <b.icon className="mx-auto h-8 w-8 text-burgundy" />
                  <h3 className="mt-3 font-serif text-xl text-olive">{b.label}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-sage">{b.example}</p>
                  <p className="mt-3 font-serif text-2xl italic text-burgundy">{b.price}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-center text-xs text-sage">
              Illustrative ranges — actual prices depend on available inventory and household needs.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.25} className="mt-16 text-center">
          <Link to="/apply-to-shop" data-testid="apply-to-shop-button" className="btn-accent !px-12 !py-4 text-base">
            Apply to Shop <ArrowRight className="h-5 w-5" />
          </Link>
          <p className="mt-4 text-sm text-sage">
            A short application helps us understand your household's needs. No detailed personal history required.
          </p>
        </FadeUp>
      </section>

      <Marquee />
    </div>
  );
}
